import React, { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Icon from '../forms/Icon';
import QuickSort from './QuickSort';
import { generateId, getLocal, mapObj, setLocal, shortHandDate, shortHandDateTime } from '../utility/functions';
import Button, { BorderLessButton } from '../forms/Button';
import TableHeader from './TableHeader';
import LoadingEllipsis from '../forms/LoadingEllipsis';
import Pager from './Pager';
import ItemsPerSelector from './ItemsPerSelector';
import Heading from '../forms/Heading';
import HamQuest from '../api/HamQuestGiver';
import DateRange from './DateRange';
import Sticker from './Sticker';
import TableData from './TableData';
import Collapsible from '../layout/Collapsable';
import Row from '../layout/Row';
import ModalFactory from '../Modal';
import Form from '../forms/Form';
import Checkbox from '../forms/Checkbox';
import OrderedInput from '../forms/OrderedInput';
import { SCREEN_MAX_WIDTH } from '../../constants/config';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 1em;
    overflow: hidden;
    
    ${({ fullscreen, theme }) => fullscreen ? `
        padding: 1em;
        background: ${theme.background};
        overflow: auto;
    ` : null}

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        min-width: unset;
    }
`;

const QuickSortWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.5em;
`;

const QuickItems = styled(Row)`
    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        justify-content: space-between;
    }
`;

const ClearFilterRow = styled(Row)`
    max-height: ${({ expand }) => expand ? '100px' : '0px'};
    min-height: ${({ expand }) => expand ? '1.5em' : '0px'};
    transition: all 0.5s;
    flex-wrap: wrap;
`;

const Error = styled.div`
    color: ${({ theme }) => theme.error};
`;

const ControllerRow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 1em;
    align-items: center;
    flex-wrap: wrap;

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        flex-wrap: wrap;
        width: 100%;
    }
`;

const slideIn = keyframes`
    0% { 
        transform: translateX(101%); 
    }
    70% { 
        transform: translateX(-3%); 
    }
    100% { 
        transform: translateX(0%); 
    }
`;

const TableCSS = styled.table`
    display: table;
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8em;
    overflow-x: clip;
    
    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        min-width: ${({ disableMobileView }) => disableMobileView ? '' : '100em'};
    }
    @media print {
        min-width: unset;
    }

    background: ${({ theme }) => theme.background};

    tbody {
        animation: ${({ disableSlideAnimation }) => disableSlideAnimation ? '' : slideIn};
        animation-duration: 0.5s;
        animation-fill-mode: forwards;
    }

    tbody tr {
        border-top: 1px solid ${({ theme }) => theme.gray}40;
        box-sizing: border-box;
        @media (max-width: ${SCREEN_MAX_WIDTH}) {
            border-top: unset;
            &:nth-child(odd) {
                background: ${({ theme }) => theme.gray}0f;
            }
        }
    }
    tbody tr:hover {
        background: ${({ theme, disableHighlightHover }) => disableHighlightHover ? 'unset' : theme.secondary + '30'};
        transition: background 0.5s;

        @media (max-width: ${SCREEN_MAX_WIDTH}) {
            background: inherit;
        }
    }

    td, th {
        box-sizing: border-box;
        flex-grow: 1;
        padding: 0.5em 1em;
        overflow: hidden;
        list-style: none;
        max-width: 10px;
        white-space: nowrap;
        text-align: left;
        vertical-align: baseline;
        box-sizing: border-box;

        ${({ colSpacing }) => colSpacing.map((e, i) => `&:nth-child(${i + 1}) { width: ${e}%;}`).join('')}
    td {
        color: ${({ theme }) => theme.gray};
    }
`;

const Tr = styled.tr`
    background: ${({ theme }) => theme.background};
    position: ${({ sticky }) => sticky ? 'sticky' : 'inherit'};
    top: 4em;
    z-index: 1;

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        position: inherit;
    }
`;

const FlexibleWrap = styled.div`
    width: 100%;
    overflow: visible;
    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        overflow: auto;
    }
`;

export const Ctd = ({ children, ...props }) => <td><div style={{ display: 'flex', justifyContent: 'center' }}>{children}</div></td>

export const TableContext = React.createContext();

//TODO: Change isReport to something more descriptive, as this only denotes that a table has a date at the top.

const Table = ({
    config: {
        tableId = '',
        data,
        getData,
        headers,
        label,
        sticker,
        quickSort,
        isReport,
        byLocation,
        searchLimit,
        colSpacing = [],
        controls,
        sticky,
        endpoint,
        disableMobileView,
        disableHeaderSearch,
        disableSlideAnimation,
        disableTableControls,
        disablePagerResults,
        disableAnimation,
        disableHighlightHover,
        disableExport,
        disableTableOptions,
        resultsPerPage,
        hideNoResults,
        renderFormat,
        firstHeaderRow,
        finalRow,
        reportCallback,
        startDate,
    },
    ...props
}) => {
    const tableRef = useRef(null);
    const optionsRef = useRef(null);

    const key = 'TABLE_LAYOUTS'
    const [tableConfig] = useState(getLocal(key) || {});
    const [id] = useState(tableId || generateId());

    const [loading, setLoading] = useState(!data);
    const [master, setMaster] = useState(data || []);
    const [tableData, setTableData] = useState(master);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(resultsPerPage ?? 15);
    const [tableHeaders, setHeaders] = useState(tableConfig[tableId] || headers);
    const [defaultHeaders, setDefaultTableConfig] = useState(null);

    const [noResultId, setNoResultId] = useState(null);
    const [filter, setFilter] = useState([]);

    const [firstRun, setFirstRun] = useState(true);

    const [isFullscreen, setFullscreen] = useState(false);
    const [error, setError] = useState(null);
    const [sort, setSort] = useState({ key: null, ascending: null });

    const [optionsHeight, setOptionsHeight] = useState(null);

    const saveTableLayoutToMemory = () => {
        setLocal(key, { [tableId]: tableHeaders });
        ModalFactory.show({
            type: 'success',
            title: 'Layout Saved'
        })
    }

    const removeTableLayoutFromMemory = () => {
        delete tableConfig[tableId];
        setLocal(key, tableConfig);
    }

    useLayoutEffect(() => {
        const spacing = [...colSpacing];
        headers.forEach((e, i) => {
            e.id = generateId();
            e.show = e.show ?? true;
            e.order = i;
        });

        const numOfHeaders = headers.reduce((a, b) => a += b.show ? 1 : 0, 0);
        if (numOfHeaders !== colSpacing.length) {
            const diff = numOfHeaders - colSpacing.length;
            const remainder = 100 - spacing.reduce((a, b) => a + b, 0);
            for (let i = 0; i < diff; i++) {
                spacing.push(remainder / diff)
            }
        }

        headers.forEach(e => {
            if (e.show) {
                e.spacing = spacing.shift();
            }
        });

        setDefaultTableConfig(JSON.parse(JSON.stringify(headers)))

        if (!tableConfig[tableId]) {
            setHeaders(headers);
        }
    }, [headers])

    useEffect(() => {
        if (!data) return;
        if (data instanceof Array) {
            data.forEach(e => e.tableID = generateId());
            setMaster(data);
        } else if (data.error !== undefined) {
            setError(data.error?.errorMessage || 'Oops, something went wrong.');
        }
    }, [data])

    useEffect(() => {
        if (getData) {
            HamQuest.get(getData)
                .then(res => {
                    if (reportCallback) {
                        reportCallback(res, setMaster)
                    } else {
                        res.forEach(e => e.tableID = generateId())
                        setMaster(res)
                    }
                })
                .catch(ex => {
                    ModalFactory.catch(ex)
                    setLoading(false)
                })
        }
    }, [getData])

    useEffect(() => {
        if (firstRun) {
            setFirstRun(false)
            return;
        }
        setLoading(false)
    }, [master])

    useEffect(() => {
        const handleFullscreenChange = () => setFullscreen(!isFullscreen);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, [isFullscreen]);

    useEffect(() => {
        if (tableRef.current && optionsRef.current) {
            const tableRect = tableRef.current.getBoundingClientRect();
            const optionsRect = optionsRef.current.getBoundingClientRect();
            const relativeTop = optionsRect.top - tableRect.top;
            setOptionsHeight(tableRef.current.offsetHeight - relativeTop);
        }
        if (sort.propKey) {
            setTableData(sortData(sort.propKey, sort.ascending, filterData(filter, master)))
        } else {
            setTableData(filterData(filter, master))
        }
        window[tableId + 'TableFilter'] = filter;
    }, [master, filter, sort])

    useEffect(() => { window[tableId + 'TableData'] = tableData }, [tableData])

    const sortData = (key, ascending, data) => {
        return [...data].sort((a, b) => {
            const valA = a[key] ?? 0;
            const valB = b[key] ?? 0;

            if (!isNaN(valA) && !isNaN(valB)) {
                return ascending ? valA - valB : valB - valA;
            } else {
                return ascending ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
            }
        });
    }

    const updateFilter = (newFilter) => {
        const { id, query, propKey, label } = newFilter;
        if (!query) {
            setFilter(filter.filter((e) => e.id !== id));
            return;
        }

        const existingFilterIndex = filter.findIndex((e) => e.propKey === propKey);

        if (existingFilterIndex === -1) {
            setFilter([...filter, newFilter]);
        } else {
            const updatedFilter = filter.map((e) =>
                e.propKey === propKey ? { ...e, id, query, propKey, label } : e
            );
            setFilter(updatedFilter);
        }
    }

    const filterData = (filters, data) => {
        let filteredList = [...data];
        let noResultId = null;

        filters.forEach(filterItem => {
            const { propKey, query, type } = filterItem;
            const dataType = type || (query.match(/[A-Z]/) ? 'caseSec' : typeof filterItem);

            let range = query.match(/<|>/) ? true : false;
            let symbol = null;
            if (range) {
                symbol = query.match(/<|>/)[0];
            }

            filteredList = filteredList.filter(item => {
                switch (dataType) {
                    case 'date':
                        if (range) {
                            const dateQuery = new Date(query.replace(/<|>/, ''));
                            return symbol === '<' ? new Date(item[propKey]) < dateQuery : new Date(item[propKey]) > dateQuery;
                        }
                        return shortHandDate(item[propKey]).includes(query.toLowerCase());
                    case 'dateTime':
                        if (range) {
                            const dateQuery = new Date(query.replace(/<|>/, ''));
                            return symbol === '<' ? new Date(item[propKey]) < dateQuery : new Date(item[propKey]) > dateQuery;
                        }
                        return shortHandDateTime(item[propKey]).includes(query.toLowerCase());
                    case 'number':
                        const numValue = parseFloat(item[propKey] ?? 0);
                        if (range) {
                            const numQuery = parseFloat(query.replace(/<|>/, ''));
                            return symbol === '<' ? numValue < numQuery : numValue > numQuery;
                        }
                        return query === '-' ? numValue === 0 : String(numValue.toFixed(2)).includes(query.replace(/[^0-9.]/g, '').trim());
                    case 'caseSec':
                        return String(item[propKey]).includes(query);
                    case 'exactMatch':
                        return String(item[propKey]) === query;
                    default:
                        return String(item[propKey]).toLowerCase().includes(query.toLowerCase());
                }
            })
            if (!noResultId && filteredList.length === 0) {
                noResultId = filterItem.id;
            }
        });
        setNoResultId(noResultId);
        return filteredList;
    }

    const order = (a, b) => a.order - b.order;
    const shown = (e) => e.show;

    const exportTable = () => {
        const useableData = tableHeaders.filter(e => e.propKey).filter(shown).sort(order);
        const dataArray = [
            useableData.map(e => e.label),
            ...tableData.map(row => (
                useableData.map(header => {
                    let value = row[header.propKey] ?? '-';

                    if (typeof value === 'string' && value.match(',')) {
                        value = value.split(',')[0];
                    }

                    return header.type === 'date'
                        ? shortHandDate(value)
                        : header.type === 'dateTime'
                            ? shortHandDateTime(value)
                            : String(value).replace(/[^\x20-\x7E\t\n\r]+|,|#/gi, '');
                })
            ))
        ];

        let title = `${document.title} - ${shortHandDate(new Date())}`
        const csvContent = `data:text/csv;charset=utf-8,${dataArray.map(e => e.join(',')).join('\n')}`;
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `${title}.csv`);
        document.body.appendChild(link);

        link.click();
        link.remove();
    }

    const toggleColumns = () => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: 'Toggle Columns',
            body: (
                <Form
                    inputs={tableHeaders.reduce((a, b) => ({ ...a, [b.id]: b.show }), { order: [] })}
                    onSubmit={e => {
                        // console.log(e)
                        const newHeaders = [...tableHeaders];
                        const { order } = e;
                        delete e.order;

                        order.forEach((e, i) => newHeaders.find(f => f.id === e).order = i)
                        mapObj(e, ([key, value]) => newHeaders.find(f => f.id === key).show = value);
                        setHeaders(newHeaders);
                        updateSpacing();
                        ModalFactory.close(id);
                    }}
                    localForm
                >
                    {({ setValue }) => {
                        const updateOrder = (e) => setValue('order', e.map(e => e.content.props.name))
                        return (
                            <OrderedInput onOrderChange={updateOrder}>
                                {[...tableHeaders]
                                    .sort(order)
                                    .map((e, i) => (
                                        <Checkbox
                                            label={e.label}
                                            name={e.id}
                                            key={`header-${i}`}
                                        />
                                    ))
                                }
                            </OrderedInput>
                        )
                    }}
                </Form>
            )
        })
    }

    const updateSpacing = (headerId, newSpacing) => {
        setHeaders((header) => {
            const newHeaders = [...header];

            if (headerId) {
                newHeaders.find(e => e.id === headerId).spacing = newSpacing;
            } else {
                const averageSize = newHeaders.filter(shown).length
                newHeaders.forEach(e => e.spacing = (100 / averageSize));
            }
            const diff = 100 - (newHeaders.filter(shown).reduce((a, b) => a += b.spacing, 0));
            if (Math.abs(diff) > 1) {
                const colRemaining = newHeaders.filter(shown).length - 1;
                newHeaders.forEach(e => {
                    if (e.id !== headerId && e.show)
                        e.spacing += (diff / colRemaining);
                });
            }
            return newHeaders
        })
    }

    const contextValue = {
        loading, setLoading,
        master, setMaster,
        tableData, setTableData,
        currentPage, setCurrentPage,
        itemsPerPage, setItemsPerPage,
        noResultId, setNoResultId,
        filter, setFilter,
        id,
        isFullscreen, setFullscreen,
        error, setError,
        sort, setSort,
        tableHeaders,
        tableConfig,
        tableId,

        setHeaders,
        defaultHeaders,

        filterData,
        updateFilter,
        sortData,
        updateSpacing,

        disablePagerResults,
        disableHeaderSearch,

        removeTableLayoutFromMemory
    }

    return (
        <TableContext.Provider value={contextValue}>
            <Wrapper
                id={id}
                fullscreen={isFullscreen}
                ref={tableRef}
            >
                {label && <Heading>{label}</Heading>}

                {isReport &&
                    <DateRange
                        route={endpoint}
                        setData={setMaster}
                        reportCallback={reportCallback}
                        searchLimit={searchLimit}
                        startDate={startDate}
                        setTableLoading={setLoading}
                        byLocation={byLocation}
                    />
                }

                {quickSort && (
                    <QuickSortWrapper>
                        <Row>
                            <Icon icon='filter' />
                            <b>Filter By:</b>
                        </Row>
                        <QuickItems wrap style={{ width: '100%' }}>
                            {quickSort.map((e, i) => <QuickSort key={`qs-${i}`} {...e} />)}
                        </QuickItems>
                    </QuickSortWrapper>
                )}
                {sticker && (
                    <Row wrap>
                        {sticker.map((e, i) => <Sticker key={`sticker-${i}`} {...e} />)}
                    </Row>
                )}

                {!disableHeaderSearch && (
                    <ClearFilterRow
                        expand={filter.length > 0}
                    >
                        {filter.map((e, i) => (
                            <BorderLessButton
                                key={`${tableId}_${i}`}
                                style={{ fontSize: '0.9em' }}
                                color={(e.id === noResultId && master.length) ? 'error' : null}
                                onClick={() => updateFilter({ id: e.id })}
                                icon='times'
                                label={<span><b>{e.label}</b> <i>({e.query})</i></span>}
                            />
                        ))}
                        {filter.length > 0 && (
                            <BorderLessButton
                                style={{ fontSize: '0.9em' }}
                                onClick={() => setFilter([])}
                                icon='trash'
                                label='Clear'
                            />
                        )}
                    </ClearFilterRow>
                )}

                {!disableTableControls &&
                    <ControllerRow>
                        <Row wrap>
                            {controls?.map(e => <Fragment key={generateId()}>{e}</Fragment>)}

                            {!disableTableOptions &&
                                <div ref={optionsRef} style={{ height: '2em', position: 'relative' }}>
                                    <div style={{ position: 'absolute', zIndex: 4, width: 'max-content' }}>
                                        <Collapsible label='Options' style={{ /*maxHeight: optionsHeight + 'px',*/ overflow: 'auto' }}>
                                            <Button
                                                label='Toggle Columns'
                                                onClick={toggleColumns}
                                            />
                                            <Button onClick={saveTableLayoutToMemory} label='Save Layout' />

                                            <Button onClick={() => {
                                                ModalFactory.confirmation({
                                                    title: 'Are you sure?',
                                                    type: 'warning',
                                                    closeAfter: true
                                                })
                                                    .then(res => {
                                                        if (!res) return;
                                                        console.log('setting default', tableHeaders, [...defaultHeaders])
                                                        setHeaders([...defaultHeaders])
                                                        removeTableLayoutFromMemory()
                                                    })
                                            }} label='Restore Default' />

                                            <Button
                                                label='Reset Spacing'
                                                onClick={() => updateSpacing()}
                                            />
                                            {!disableExport && (
                                                <Button
                                                    icon='download'
                                                    onClick={() => exportTable()}
                                                    label='Export'
                                                />
                                            )}
                                            <Button
                                                icon={isFullscreen ? 'compress' : 'expand'}
                                                onClick={() => {
                                                    const fullscreenElement = document.getElementById(id);
                                                    if (!document.fullscreenElement) {
                                                        fullscreenElement.requestFullscreen()
                                                            .catch(err => console.error('Error attempting to enable full-screen mode:', err));
                                                    } else {
                                                        document.exitFullscreen();
                                                    }
                                                }}
                                                label={isFullscreen ? 'Exit' : 'Fullscreen'}
                                            />
                                        </Collapsible>
                                    </div>
                                </div>
                            }

                        </Row>

                        <ControllerRow>
                            <ItemsPerSelector />
                            <Pager />
                        </ControllerRow>
                    </ControllerRow>
                }
                <FlexibleWrap>
                    <TableCSS
                        disableMobileView={disableMobileView}
                        disableSlideAnimation={disableSlideAnimation}
                        disableHighlightHover={disableHighlightHover}
                        colSpacing={tableHeaders.filter(shown).sort(order).map(e => e.spacing)}
                        {...props}
                    >
                        <thead>
                            {firstHeaderRow && firstHeaderRow()}
                            <Tr sticky={sticky}                        >
                                {tableHeaders.filter(shown).sort(order).map((e, i) => <TableHeader key={`tH-${i}`} index={i} {...e} />)}
                            </Tr>
                        </thead>
                        {!error && <TableData renderFormat={renderFormat} />}
                        {finalRow && (
                            <tbody>{
                                finalRow(
                                    tableData
                                        .slice((currentPage - 1) * itemsPerPage, ((currentPage - 1) * itemsPerPage) + itemsPerPage),
                                    tableData
                                )
                            }</tbody>
                        )}
                    </TableCSS>
                </FlexibleWrap>
                {
                    error ? <Error>{error}</Error> : tableData.length === 0 &&
                        <Heading>
                            {loading ? <><Icon icon='spinner' /> Loading<LoadingEllipsis /></> : !hideNoResults ? 'No Results.' : null}
                        </Heading>
                }
            </Wrapper>
        </TableContext.Provider>
    );
}


export default Table;